import type { FC } from 'react'
import Head from 'next/head'
import { Currency } from '@model/product/PriceInfo'
import { getFullImageUrl } from '@utils/getFullImageUrl'
import { hasCloudinaryImages, getFirstCloudinaryImage } from '@utils/product/cloudinary-images-utils'
import type { Product } from '@model/product'
import { cloudinaryImageGeneration } from '@utils/cloudinary-image-generation'

export interface HelmetProps {
  title: string
  description?: string
  type: 'product' | 'category' | 'collection' | 'website'
  imageUrl?: string
  product?: Product
  url?: string
  locale?: string
  price?: string
  currency?: Currency
  brand?: string
  stock?: string
  condition?: string
  sku?: string
}

const defaultImage = 'https://shopping.tallink.com/v1/page-image/et?store=store2'

const Helmet: FC<HelmetProps> = (props) => {
  const { title, description, type, imageUrl, product, url, locale, price, currency, brand, stock, condition, sku } =
    props

  const getImageUrl = () => {
    if (product && hasCloudinaryImages(product)) {
      const cloudinaryImage = getFirstCloudinaryImage(product)
      return cloudinaryImage ? cloudinaryImageGeneration(cloudinaryImage) : null
    }
    if (imageUrl) {
      return getFullImageUrl(imageUrl || defaultImage, true)
    }
    return null
  }

  const finalImageUrl = getImageUrl()

  return (
    <Head>
      <title>{title}</title>
      {description && <meta name="description" content={description} />}
      {title && <meta property="og:title" content={title} />}
      {type && <meta property="og:type" content={type} />}
      {finalImageUrl && <meta property="og:image" content={finalImageUrl} />}
      {description && <meta property="og:description" content={description} />}
      {url && <meta property="og:url" content={url} />}
      {locale && <meta property="og:locale" content={locale} />}
      {price && <meta property="og:price:amount" content={price} />}
      {currency && <meta property="og:price:currency" content={currency} />}
      {brand && <meta property="product:brand" content={brand} />}
      {stock && <meta property="product:availability" content={stock} />}
      {condition && <meta property="product:condition" content={condition} />}
      {locale && <meta property="product:locale" content={locale} />}
      {price && <meta property="product:price:amount" content={price} />}
      {currency && <meta property="product:price:currency" content={currency} />}
      {sku && <meta property="product:retailer_item_id" content={sku} />}
      <meta name="twitter:card" content="summary" />
      <meta name="twitter:site" content="@TallinkSilja" />
    </Head>
  )
}

export default Helmet
